import React from 'react';

import {
  Edit,
  usePermissions,
  TabbedShowLayout,
  Labeled,
  useRecordContext,
  List,
  ListActions,
} from 'react-admin';

import {
  Card,
  SxProps,
} from '@mui/material';

import useCustomEditView from '../../hooks/useCustomEditView';

import CarOwnership from '../../customFields/CarOwnership';

import actions, { Actions } from '../../constants/actions';

import hasAccess from '../../utilities/hasAccess';

import IndividualBreadcrumb from './IndividualBreadcrumb';

import Transaction from '../transaction';
import Categorization from '../categorization';
import KycForm from '../kycForm';

import useValidateId from '../../hooks/useValidateId';

import Information from './editTabs/Information';
import KycDataTabs from './editTabs/kyc/KycDataTabs';

import resourceTypes from '../../constants/resourceTypes';
import { entityCleaner } from '../../utilities/dataProvider/cleaner';
import reportTypes from '../../constants/reportTypes';
import ReportModal from '../../customFields/ReportModal';
import SmartDateTimeInput from '../../customFields/SmartDateTimeInput';
import { attributesSchema, checkIsTypeAllowed } from '../../utilities/reports/permissions';
import ConnectedProducts from '../product/ConnectedProducts';
import ConnectedAccounts from '../accountEntity/ConnectedAccounts';
import AuditResourceList from '../audit/AuditResourceList';
import dateParse, { dateFormat } from '../utils/dateParser';
import EditToolbar from '../layout/EditToolbar';
import { ReportListDatagrid } from '../reports/ReportHistoryTable';

const customEditSx = (isCustomView: boolean): SxProps => (
  isCustomView
    ? { '& .RaEdit-card': { backgroundColor: 'transparent', boxShadow: 'none', marginTop: 0 } }
    : { '& .RaEdit-card': { marginTop: 0 } }
);

export const IndividualEditContent = () => {
  const { permissions } = usePermissions<Actions[]>();
  const record = useRecordContext();
  const { isCustomView } = useCustomEditView();

  if (!record) return null;

  return (
    <TabbedShowLayout
      sx={isCustomView ? { display: 'none', marginTop: 0 } : { marginTop: 0 }}
    >
      <TabbedShowLayout.Tab label="Information">
        <Labeled label={false} sx={{ display: 'flex' }}>
          <Information />
        </Labeled>
      </TabbedShowLayout.Tab>
      <TabbedShowLayout.Tab
        label="KYC data"
        path="kyc-data"
      >
        <KycDataTabs />
      </TabbedShowLayout.Tab>
      {hasAccess(permissions, actions.FORM_ANSWER_GET) && (
        <TabbedShowLayout.Tab label="KYC form" path="form-answers/*">
          <KycForm />
        </TabbedShowLayout.Tab>
      )}
      {hasAccess(permissions, actions.CAR_GET) && (
        <TabbedShowLayout.Tab label="Car ownership" path="cars">
          <CarOwnership />
        </TabbedShowLayout.Tab>
      )}
      {hasAccess(permissions, actions.REQUEST_GET) && (
        <TabbedShowLayout.Tab label="Transactions" path="transactions">
          <Transaction />
        </TabbedShowLayout.Tab>
      )}
      {hasAccess(permissions, actions.ACCOUNT_ENTITY_GET) && (
        <TabbedShowLayout.Tab label="Connected accounts" path="accounts">
          <ConnectedAccounts />
        </TabbedShowLayout.Tab>
      )}
      {hasAccess(permissions, actions.PRODUCT_GET) && (
        <TabbedShowLayout.Tab label="Connected products" path="products">
          <ConnectedProducts />
        </TabbedShowLayout.Tab>
      )}
      {hasAccess(permissions, actions.BANK_DATA_GET_CATEGORIZATION) && (
        <TabbedShowLayout.Tab label="Categorization" path="categorization">
          <Categorization />
        </TabbedShowLayout.Tab>
      )}
      {hasAccess(permissions, actions.AUDIT_TRACK_LIST) && (
        <TabbedShowLayout.Tab label="Audit trail" path="audit-tracks">
          <AuditResourceList resourceType={resourceTypes.INDIVIDUAL} />
        </TabbedShowLayout.Tab>
      )}
      {hasAccess(permissions, actions.REPORT_LIST, {
        schema: attributesSchema,
        check: checkIsTypeAllowed(reportTypes.ENTITY_REPORT),
      }) && (
        <TabbedShowLayout.Tab label="Reports" path="reports">
          <List
            storeKey={false}
            disableSyncWithLocation
            actions={<ListActions hasCreate={false} />}
            perPage={10}
            empty={false}
            pagination={false}
            exporter={false}
            resource="reports"
            sort={{ field: 'createdAt', order: 'DESC' }}
            filters={[
              <SmartDateTimeInput
                label="From Date"
                source="dateFrom"
                parse={dateParse}
                format={dateFormat}
                sx={{
                  '& .MuiFormHelperText-root': {
                    display: 'unset',
                  },
                }}
              />,
              <SmartDateTimeInput
                label="To Date"
                source="dateTo"
                parse={dateParse}
                format={dateFormat}
                sx={{
                  '& .MuiFormHelperText-root': {
                    display: 'unset',
                  },
                }}
              />,
            ]}
            filter={{ type: reportTypes.ENTITY_REPORT, entityId: record.id }}
            queryOptions={{ refetchInterval: 5000 }}
          >
            <Card variant="outlined">
              <ReportListDatagrid />
            </Card>
          </List>
        </TabbedShowLayout.Tab>
      )}
    </TabbedShowLayout>
  );
};

const EditActions = () => (
  <EditToolbar>
    <ReportModal />
  </EditToolbar>
);

const IndividualsEdit = () => {
  const { isCustomView } = useCustomEditView();

  const validatedId = useValidateId();
  if (!validatedId.isValid) return null;

  return (
    <Edit
      mutationMode="pessimistic"
      redirect={false}
      sx={customEditSx(isCustomView)}
      transform={entityCleaner}
      actions={<EditActions />}
    >
      <IndividualBreadcrumb />
      <IndividualEditContent />
    </Edit>
  );
};

export default IndividualsEdit;
